import React from 'react';
import { Navigation, Home } from 'lucide-react';

interface CompassIndicatorProps {
  orientation: number; // Degrees from 0-360, where 0 = North
  size?: number;
  showDegrees?: boolean;
  className?: string;
}

const CompassIndicator: React.FC<CompassIndicatorProps> = ({
  orientation,
  size = 120,
  showDegrees = true,
  className = ''
}) => {
  // Normalize orientation to 0-360 range
  const normalizedOrientation = ((orientation % 360) + 360) % 360;
  
  // Calculate rotation for the compass needle (pointing to the orientation)
  const needleRotation = normalizedOrientation;
  
  // Get cardinal direction
  const getCardinalDirection = (degrees: number) => {
    const normalized = ((degrees % 360) + 360) % 360;
    if (normalized >= 337.5 || normalized < 22.5) return 'N';
    if (normalized >= 22.5 && normalized < 67.5) return 'NE';
    if (normalized >= 67.5 && normalized < 112.5) return 'E';
    if (normalized >= 112.5 && normalized < 157.5) return 'SE';
    if (normalized >= 157.5 && normalized < 202.5) return 'S';
    if (normalized >= 202.5 && normalized < 247.5) return 'SW';
    if (normalized >= 247.5 && normalized < 292.5) return 'W';
    if (normalized >= 292.5 && normalized < 337.5) return 'NW';
    return 'N';
  };

  const cardinalDirection = getCardinalDirection(normalizedOrientation);

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div 
        className="relative bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200/50 rounded-full shadow-2xl ring-4 ring-white/50 backdrop-blur-sm"
        style={{ width: size, height: size }}
      >
        {/* Outer ring with tick marks */}
        <div className="absolute inset-1 rounded-full bg-gradient-to-br from-slate-50 via-white to-slate-100 shadow-inner">
          {/* Degree tick marks */}
          {Array.from({ length: 12 }, (_, i) => {
            const angle = i * 30;
            const isCardinal = angle % 90 === 0;
            return (
              <div
                key={i}
                className="absolute w-0.5 bg-gradient-to-b from-slate-400 to-slate-600 rounded-full"
                style={{
                  height: isCardinal ? `${size * 0.08}px` : `${size * 0.04}px`,
                  top: `${size * 0.02}px`,
                  left: '50%',
                  transformOrigin: `0 ${size * 0.46}px`,
                  transform: `translateX(-50%) rotate(${angle}deg)`
                }}
              />
            );
          })}
        </div>

        {/* Inner compass background */}
        <div className="absolute inset-4 rounded-full bg-gradient-to-br from-blue-50/80 via-sky-50/60 to-cyan-50/80 border border-blue-200/30 backdrop-blur-sm shadow-inner">
          {/* Cardinal direction markers */}
          <div className="absolute inset-0 rounded-full">
            {/* North */}
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
              <div className="px-2 py-1 bg-gradient-to-b from-red-500 to-red-600 text-white text-xs font-bold rounded-full shadow-lg ring-2 ring-red-200/50">
                N
              </div>
            </div>
            {/* East */}
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <div className="px-2 py-1 bg-gradient-to-b from-slate-500 to-slate-600 text-white text-xs font-bold rounded-full shadow-md">
                E
              </div>
            </div>
            {/* South */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2">
              <div className="px-2 py-1 bg-gradient-to-b from-slate-500 to-slate-600 text-white text-xs font-bold rounded-full shadow-md">
                S
              </div>
            </div>
            {/* West */}
            <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
              <div className="px-2 py-1 bg-gradient-to-b from-slate-500 to-slate-600 text-white text-xs font-bold rounded-full shadow-md">
                W
              </div>
            </div>
          </div>

          {/* Center hub */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800 rounded-full shadow-lg ring-2 ring-white/20">
            <div className="absolute inset-1 bg-gradient-to-br from-slate-300 to-slate-400 rounded-full"></div>
          </div>
          
          {/* Compass needle pointing to orientation */}
          <div 
            className="absolute top-1/2 left-1/2 origin-bottom transform -translate-x-1/2 -translate-y-full transition-all duration-700 ease-out"
            style={{ 
              transform: `translate(-50%, -100%) rotate(${needleRotation}deg)`,
              height: `${size * 0.32}px`
            }}
          >
            {/* Needle shaft */}
            <div className="relative w-1.5 h-full bg-gradient-to-t from-red-700 via-red-500 to-red-300 rounded-full shadow-lg">
              {/* Needle tip */}
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-3 border-r-3 border-b-4 border-transparent border-b-red-600 drop-shadow-sm"></div>
              {/* Needle highlight */}
              <div className="absolute top-0 left-0 w-0.5 h-full bg-gradient-to-t from-transparent via-red-200 to-white/60 rounded-full"></div>
            </div>
          </div>

          {/* Property/Front Door indicator */}
          <div 
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-700 ease-out"
            style={{ transform: `translate(-50%, -50%) rotate(${needleRotation}deg) translateY(-${size * 0.25}px)` }}
          >
            <div className="p-2 bg-gradient-to-br from-amber-400 via-yellow-500 to-orange-500 rounded-full shadow-lg ring-2 ring-amber-200/50 backdrop-blur-sm">
              <Home size={14} className="text-white drop-shadow-sm" />
            </div>
          </div>

          {/* Decorative inner rings */}
          <div className="absolute inset-6 rounded-full border border-blue-300/20"></div>
          <div className="absolute inset-8 rounded-full border border-blue-400/15"></div>
        </div>
      </div>

      {/* Enhanced direction and degree display */}
      <div className="mt-4 text-center">
        <div className="px-4 py-2 bg-gradient-to-r from-slate-100 via-white to-slate-100 border border-slate-200/50 rounded-full shadow-md backdrop-blur-sm">
          <div className="text-xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
            {cardinalDirection}
          </div>
          {showDegrees && (
            <div className="text-sm font-medium text-slate-600 mt-1">
              {normalizedOrientation.toFixed(0)}°
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompassIndicator; 